import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  Calendar,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileText,
  CreditCard,
  Package,
  UserCheck,
  DollarSign,
  Building2,
  Home,
  ShoppingCart,
  BarChart3,
  Receipt,
  Layers,
  Tag,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Printer,
  Calculator,
  Wallet,
  Receipt as ReceiptIcon,
  TrendingDown,
  Bell,
  Activity,
  PlusCircle,
  Download,
  Send,
  Info,
  Box,
  Package2,
  Moon,
  Sun,
  X,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';

const Expenses = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { formatCurrency, currencySymbol } = useSettings();
  const navigate = useNavigate();
  
  // Expenses data
  const [expenses, setExpenses] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseStats, setExpenseStats] = useState({
    total_expenses: 0,
    total_amount: 0,
    this_month: 0,
    today: 0
  });

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    payment_method: 'all',
    date_range: 'all',
    sort_by: 'created_at'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Form states
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: '',
    payment_method: 'cash',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
    // Payment method specific fields
    cash_breakdown: {},
    bank_name: '',
    account_name: '',
    account_number: '',
    mobile_network: '',
    sender_mobile_number: '',
    receiver_mobile_number: '',
    transaction_reference: '',
    card_number: ''
  });

  // Search and filter states
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Expense categories
  const expenseCategories = [
    'Maintenance',
    'Office Supplies',
    'Petty Cash',
    'Professional Fees',
    'Salary Advance',
    'Staff Salary',
    'Utilities',
    'Rent',
    'Marketing',
    'Travel',
    'Equipment',
    'Insurance',
    'Professional Services',
    'Software/Subscriptions',
    'Other'
  ];

  // Payment methods
  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'check', label: 'Check' },
    { value: 'mobile_money', label: 'Mobile Money' }
  ];

  // Fetch expenses
  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearch,
        category: filters.category !== 'all' ? filters.category : '',
        payment_method: filters.payment_method !== 'all' ? filters.payment_method : '',
        date_range: filters.date_range !== 'all' ? filters.date_range : ''
      };

      try {
        const response = await apiService.getExpenses(queryParams);
        setExpenses(response.data || []);
        setTotalPages(response.last_page || 1);
        setTotalItems(response.total || 0);
      } catch (apiError) {
        // Use mock data if API fails
        const mockExpenses = generateMockExpenses();
        const filteredExpenses = applyFilters(mockExpenses);
        const paginatedExpenses = applyPagination(filteredExpenses);
        
        setExpenses(paginatedExpenses);
        setTotalPages(Math.ceil(filteredExpenses.length / perPage));
        setTotalItems(filteredExpenses.length);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Failed to load expenses');
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, perPage, debouncedSearch, filters]);

  // Generate mock expenses for testing
  const generateMockExpenses = () => {
    const categories = expenseCategories;
    const methods = paymentMethods;
    
    return Array.from({ length: 25 }, (_, index) => ({
      id: index + 1,
      description: `Expense ${index + 1}`,
      amount: (Math.random() * 1000 + 50).toFixed(2),
      category: categories[Math.floor(Math.random() * categories.length)],
      payment_method: methods[Math.floor(Math.random() * methods.length)].value,
      expense_date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      vendor: `Vendor ${index + 1}`,
      notes: `Notes for expense ${index + 1}`,
      reference_number: `REF-${String(index + 1).padStart(3, '0')}`,
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString()
    }));
  };

  // Fetch expense statistics
  const fetchExpenseStats = useCallback(async () => {
    try {
      try {
        const response = await apiService.getExpenseStats();
        const stats = response.data;
        
        setExpenseStats({
          total_expenses: stats.total_expenses || 0,
          total_amount: stats.total_amount || 0,
          this_month: stats.this_month || 0,
          today: stats.today || 0,
          monthly_change: stats.monthly_change || 0,
          daily_change: stats.daily_change || 0
        });
      } catch (apiError) {
        // Use mock stats if API fails
        setExpenseStats({
          total_expenses: 25,
          total_amount: '12947.93',
          this_month: '12071.47',
          today: '0.00',
          monthly_change: 8,
          daily_change: 15
        });
      }
    } catch (error) {
      console.error('Error fetching expense stats:', error);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    fetchExpenseStats();
  }, [fetchExpenseStats]);

  // Handler functions
  const handleViewExpense = (expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      description: expense.paid_to,
      amount: expense.amount,
      category: expense.expense_account,
      payment_method: expense.mode_of_payment,
      expense_date: expense.expense_date,
      notes: expense.description || '',
      // Payment method specific fields
      cash_breakdown: expense.cash_breakdown || {},
      bank_name: expense.bank_name || '',
      account_name: expense.account_name || '',
      account_number: expense.account_number || '',
      mobile_network: expense.mobile_network || '',
      sender_mobile_number: expense.sender_mobile_number || '',
      receiver_mobile_number: expense.receiver_mobile_number || '',
      transaction_reference: expense.transaction_reference || '',
      card_number: expense.card_number || ''
    });
    setShowEditModal(true);
  };

  const handlePrintReceipt = (expense) => {
    setSelectedExpense(expense);
    setShowPrintModal(true);
  };

  const handleDeleteExpense = async () => {
    if (!selectedExpense) return;
    
    setDeleteLoading(true);
    try {
      await apiService.deleteExpense(selectedExpense.id);
      toast.success('Expense deleted successfully!', {
        duration: 3000,
        icon: '🗑️',
        style: {
          background: '#10B981',
          color: 'white',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      
      setShowDeleteModal(false);
      setSelectedExpense(null);
      fetchExpenses();
      fetchExpenseStats();
      
    } catch (error) {
      console.error('Error deleting expense:', error);
      if (error.message === 'Authentication failed') {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error('Failed to delete expense. Please try again.', {
          duration: 3000,
          icon: '❌',
          style: {
            background: '#EF4444',
            color: 'white',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = (expense) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  // Form handlers
  const handleExpenseFormChange = (field, value) => {
    setExpenseForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear related errors when field changes
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const updateCashBreakdown = (denomination, count) => {
    setExpenseForm(prev => ({
      ...prev,
      cash_breakdown: {
        ...prev.cash_breakdown,
        [`${denomination}_notes`]: parseInt(count) || 0
      }
    }));
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };


  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Form validation
  const validateExpenseForm = () => {
    const errors = {};
    
    if (!expenseForm.description.trim()) {
      errors.description = 'Paid To / Received By is required';
    }
    
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }
    
    if (!expenseForm.category) {
      errors.category = 'Category is required';
    }
    
    if (!expenseForm.payment_method) {
      errors.payment_method = 'Payment method is required';
    }
    
    if (!expenseForm.expense_date) {
      errors.expense_date = 'Expense date is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateExpenseForm()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      // Prepare data for API
      const expenseData = {
        paid_to: expenseForm.description,
        description: expenseForm.notes,
        amount: parseFloat(expenseForm.amount),
        expense_date: expenseForm.expense_date,
        expense_account: expenseForm.category,
        mode_of_payment: expenseForm.payment_method,
        user_id: 1, // Default user ID
        // Payment method specific fields
        cash_breakdown: expenseForm.payment_method === 'cash' ? expenseForm.cash_breakdown : null,
        bank_name: expenseForm.payment_method === 'bank_transfer' ? expenseForm.bank_name : null,
        account_name: expenseForm.payment_method === 'bank_transfer' ? expenseForm.account_name : null,
        account_number: expenseForm.payment_method === 'bank_transfer' ? expenseForm.account_number : null,
        mobile_network: expenseForm.payment_method === 'mobile_money' ? expenseForm.mobile_network : null,
        sender_mobile_number: expenseForm.payment_method === 'mobile_money' ? expenseForm.sender_mobile_number : null,
        receiver_mobile_number: expenseForm.payment_method === 'mobile_money' ? expenseForm.receiver_mobile_number : null,
        transaction_reference: expenseForm.transaction_reference,
        card_number: expenseForm.payment_method === 'card' ? expenseForm.card_number : null,
      };
      
      if (showEditModal && selectedExpense) {
        // Update existing expense
        const response = await apiService.updateExpense(selectedExpense.id, expenseData);
        
        // Cash analysis tracking disabled - expenses are tracked separately
        // The expense is already recorded in the expense table
        
        toast.success('Expense updated successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
        setShowEditModal(false);
      } else {
        // Create new expense
        const response = await apiService.createExpense(expenseData);
        
        // Cash analysis tracking disabled - expenses are tracked separately
        // The expense is already recorded in the expense table
        
        toast.success('Expense added successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
        setShowExpenseModal(false);
      }
      
      // Reset form
      setSelectedExpense(null);
      setFormErrors({});
      setExpenseForm({
        description: '',
        amount: '',
        category: '',
        payment_method: 'cash',
        expense_date: new Date().toISOString().split('T')[0],
        vendor: '',
        notes: '',
        reference_number: ''
      });
      
      // Refresh data
      fetchExpenses();
      fetchExpenseStats();
      
    } catch (error) {
      console.error('Error processing expense:', error);
      const action = showEditModal ? 'update' : 'add';
      if (error.message === 'Authentication failed') {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(`Failed to ${action} expense. Please try again.`, {
          duration: 3000,
        });
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update filters when debounced search changes
  useEffect(() => {
    if (searchInput !== debouncedSearch) {
      setIsSearching(true);
    }
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
    setCurrentPage(1);
  }, [debouncedSearch, searchInput]);


  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row mt-16 sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Expenses</h1>
                  <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Manage and track your business expenses</p>
                </div>
                <button
                  onClick={() => {
                    setExpenseForm({
                      description: '',
                      amount: '',
                      category: '',
                      payment_method: 'cash',
                      expense_date: new Date().toISOString().split('T')[0],
                      notes: '',
                      // Payment method specific fields
                      cash_breakdown: {},
                      bank_name: '',
                      account_name: '',
                      account_number: '',
                      mobile_network: '',
                      sender_mobile_number: '',
                      receiver_mobile_number: '',
                      transaction_reference: '',
                      card_number: ''
                    });
                    setShowExpenseModal(true);
                  }}
                  className="inline-flex items-center px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto justify-center"
                >
                  <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  <span className="text-sm md:text-base">Add Expense</span>
                </button>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 md:p-5 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full -translate-y-8 translate-x-8 opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-blue-600 mb-1">Total Expenses</p>
                      <p className="text-2xl md:text-3xl font-bold text-blue-900">{expenseStats.total_expenses}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Receipt className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                      <TrendingUp className="h-3 w-3 text-green-600" />
                    </div>
                    <span className={`text-xs font-semibold ${parseFloat(expenseStats.monthly_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(expenseStats.monthly_change || 0) > 0 ? '+' : ''}{parseFloat(expenseStats.monthly_change || 0)}% from last month
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 md:p-5 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full -translate-y-8 translate-x-8 opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Total Amount</p>
                      <p className="text-2xl md:text-3xl font-bold text-green-900">{formatCurrency(expenseStats.total_amount || 0)}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                      <TrendingUp className="h-3 w-3 text-green-600" />
                    </div>
                    <span className={`text-xs font-semibold ${parseFloat(expenseStats.monthly_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(expenseStats.monthly_change || 0) > 0 ? '+' : ''}{parseFloat(expenseStats.monthly_change || 0)}% from last month
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 md:p-5 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full -translate-y-8 translate-x-8 opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-purple-600 mb-1">This Month</p>
                      <p className="text-2xl md:text-3xl font-bold text-purple-900">{formatCurrency(expenseStats.this_month || 0)}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-red-100 rounded-md flex items-center justify-center">
                      <TrendingUp className="h-3 w-3 text-red-600 rotate-180" />
                    </div>
                    <span className={`text-xs font-semibold ${parseFloat(expenseStats.daily_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(expenseStats.daily_change || 0) > 0 ? '+' : ''}{parseFloat(expenseStats.daily_change || 0)} from yesterday
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 md:p-5 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full -translate-y-8 translate-x-8 opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">Today</p>
                      <p className="text-2xl md:text-3xl font-bold text-orange-900">{formatCurrency(expenseStats.today || 0)}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                      <TrendingUp className="h-3 w-3 text-green-600" />
                    </div>
                    <span className={`text-xs font-semibold ${parseFloat(expenseStats.daily_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(expenseStats.daily_change || 0) > 0 ? '+' : ''}{parseFloat(expenseStats.daily_change || 0)} from yesterday
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Filter className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Filters & Search</h2>
                  <p className="text-gray-600 text-sm">Find and filter your expenses</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                      placeholder="Search expenses..."
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  >
                    <option value="all">All Categories</option>
                    {expenseCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <select
                    value={filters.payment_method}
                    onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  >
                    <option value="all">All Methods</option>
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={filters.date_range}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_range: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Expenses Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-4 md:mb-6">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Expenses</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={fetchExpenses}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
                      title="Refresh"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                <table className="w-full divide-y divide-gray-200 text-sm md:text-base min-w-[800px]">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">
                        Paid To
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px] max-w-[300px]">
                        Description
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">
                        Category
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[80px]">
                        Amount
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[80px]">
                        Method
                      </th>
                      <th className="px-2 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">
                        Date
                      </th>
                      <th className="px-2 md:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-3 md:px-6 py-8 md:py-12 text-center">
                          <div className="flex items-center justify-center">
                            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                            <span className="ml-2 text-gray-500">Loading expenses...</span>
                          </div>
                        </td>
                      </tr>
                    ) : expenses.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-3 md:px-6 py-8 md:py-12 text-center">
                          <div className="text-gray-500">
                            <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-lg font-medium">No expenses found</p>
                            <p className="text-sm">Get started by adding your first expense</p>
                            <button
                              onClick={() => setShowExpenseModal(true)}
                              className="mt-4 bg-gradient-to-r from-red-500 to-blue-600 hover:from-red-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-all duration-200"
                            >
                              <Plus className="w-4 h-4" />
                              <span>Add Expense</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      expenses.map((expense) => (
                        <tr key={expense.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                          <td className="px-2 md:px-4 py-3">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-red-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Receipt className="h-4 w-4 text-red-600" />
                              </div>
                              <div className="ml-2 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate" title={expense.paid_to}>
                                  {expense.paid_to}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <div className="text-sm text-gray-900 break-words" title={expense.description || 'No description'}>
                              {expense.description || 'No description'}
                            </div>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {expense.expense_account}
                            </span>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <div className="text-sm font-semibold text-red-600">
                              {formatCurrency(expense.amount)}
                            </div>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {paymentMethods.find(m => m.value === expense.mode_of_payment)?.label || expense.mode_of_payment}
                            </span>
                          </td>
                          <td className="px-2 md:px-4 py-3 text-sm text-gray-900">
                            {new Date(expense.expense_date).toLocaleDateString()}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button 
                                onClick={() => handleViewExpense(expense)}
                                className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="View Expense Details"
                              >
                                <Eye size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  View
                                </span>
                              </button>
                              <button 
                                onClick={() => handleEditExpense(expense)}
                                className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="Edit Expense"
                              >
                                <Edit size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Edit
                                </span>
                              </button>
                              <button 
                                onClick={() => handlePrintReceipt(expense)}
                                className="group/btn relative p-2 text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="Print Receipt"
                              >
                                <Printer size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Receipt
                                </span>
                              </button>
                              <button 
                                onClick={() => handleDeleteClick(expense)}
                                className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="Delete Expense"
                              >
                                <Trash2 size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Delete
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gradient-to-r from-gray-50 to-white px-4 md:px-6 py-3 md:py-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="text-sm text-gray-700">
                      Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
                    </span>
                    <select
                      value={perPage}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value={10}>10 per page</option>
                      <option value={15}>15 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-center sm:justify-end space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 md:px-4 py-2 border border-gray-300 rounded-xl text-xs md:text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ArrowLeft size={16} className="inline mr-1" />
                      Previous
                    </button>
                    <span className="px-3 md:px-4 py-2 text-xs md:text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 md:px-4 py-2 border border-gray-300 rounded-xl text-xs md:text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Next
                      <ArrowRight size={16} className="inline ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/* Expense Modal */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-t-xl flex items-center justify-between">
                <h3 className="text-lg font-semibold">Add New Expense</h3>
                <button
                  onClick={() => setShowExpenseModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Paid To / Received By *</label>
                    <input
                      type="text"
                      value={expenseForm.description}
                      onChange={(e) => handleExpenseFormChange('description', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter person or entity name"
                    />
                    {formErrors.description && <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => handleExpenseFormChange('amount', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.amount ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                    {formErrors.amount && <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={expenseForm.category}
                      onChange={(e) => handleExpenseFormChange('category', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.category ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select category</option>
                      {expenseCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    {formErrors.category && <p className="mt-1 text-sm text-red-600">{formErrors.category}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <select
                      value={expenseForm.payment_method}
                      onChange={(e) => handleExpenseFormChange('payment_method', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="card">Card</option>
                      <option value="cheque">Cheque</option>
                    </select>
                    {formErrors.payment_method && <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expense Date *</label>
                    <input
                      type="date"
                      value={expenseForm.expense_date}
                      onChange={(e) => handleExpenseFormChange('expense_date', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.expense_date ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.expense_date && <p className="mt-1 text-sm text-red-600">{formErrors.expense_date}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={expenseForm.notes}
                      onChange={(e) => handleExpenseFormChange('notes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                      placeholder="Notes (optional)"
                    />
                  </div>
                </div>

                {/* Dynamic Payment Method Fields */}
                <div className="mt-6">
                  {/* Cash Breakdown */}
                  {expenseForm.payment_method === 'cash' && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{currencySymbol}</span>
                        </span>
                        Cash Breakdown
                      </h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => (
                          <div key={denomination} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="text-center">
                              <span className="text-sm font-medium text-gray-700 block mb-2">{currencySymbol} {denomination}</span>
                              <input
                                type="number"
                                min="0"
                                value={expenseForm.cash_breakdown[`${denomination}_notes`] || 0}
                                onChange={(e) => updateCashBreakdown(denomination, e.target.value)}
                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                              />
                              <span className="text-xs font-medium text-gray-900 block mt-1">
                                {formatCurrency((expenseForm.cash_breakdown[`${denomination}_notes`] || 0) * denomination)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <div className="text-center">
                          <span className="text-lg font-bold text-green-600">
                            Total: {formatCurrency(Object.keys(expenseForm.cash_breakdown).reduce((total, key) => {
                              const denomination = parseInt(key.split('_')[0]);
                              const count = expenseForm.cash_breakdown[key] || 0;
                              return total + (denomination * count);
                            }, 0))}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Fields */}
                  {expenseForm.payment_method === 'bank_transfer' && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">🏦</span>
                        </span>
                        Bank Transfer Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={expenseForm.bank_name}
                            onChange={(e) => handleExpenseFormChange('bank_name', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter bank name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Name
                          </label>
                          <input
                            type="text"
                            value={expenseForm.account_name}
                            onChange={(e) => handleExpenseFormChange('account_name', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter account name"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.account_number}
                            onChange={(e) => handleExpenseFormChange('account_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter account number"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mobile Money Fields */}
                  {expenseForm.payment_method === 'mobile_money' && (
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">📱</span>
                        </span>
                        Mobile Money Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mobile Network
                          </label>
                          <select
                            value={expenseForm.mobile_network}
                            onChange={(e) => handleExpenseFormChange('mobile_network', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          >
                            <option value="">Select Network</option>
                            <option value="mtn">MTN</option>
                            <option value="vodafone">Vodafone</option>
                            <option value="airteltigo">AirtelTigo</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sender Mobile Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.sender_mobile_number}
                            onChange={(e) => handleExpenseFormChange('sender_mobile_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter sender number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Receiver Mobile Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.receiver_mobile_number}
                            onChange={(e) => handleExpenseFormChange('receiver_mobile_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter receiver number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Transaction Reference
                          </label>
                          <input
                            type="text"
                            value={expenseForm.transaction_reference}
                            onChange={(e) => handleExpenseFormChange('transaction_reference', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter transaction reference"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card Payment Fields */}
                  {expenseForm.payment_method === 'card' && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">💳</span>
                        </span>
                        Card Payment Details
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Number (Last 4 digits)
                        </label>
                        <input
                          type="text"
                          value={expenseForm.card_number}
                          onChange={(e) => handleExpenseFormChange('card_number', e.target.value)}
                          className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          placeholder="Enter last 4 digits"
                          maxLength="4"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  onClick={() => {
                    setShowExpenseModal(false);
                    setFormErrors({});
                  }}
                  className="px-3 md:px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExpenseSubmit}
                  disabled={formLoading}
                  className="px-3 md:px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 w-full sm:w-auto shadow-md"
                >
                  {formLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Add Expense</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-t-xl flex items-center justify-between">
                <h3 className="text-lg font-semibold">Expense Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Basic Expense Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Paid To</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-gray-900">{selectedExpense.paid_to}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-gray-900 font-semibold">{formatCurrency(selectedExpense.amount || 0)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {selectedExpense.expense_account}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {paymentMethods.find(m => m.value === selectedExpense.mode_of_payment)?.label || selectedExpense.mode_of_payment}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-gray-900">{new Date(selectedExpense.expense_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference</label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-gray-900">{selectedExpense.transaction_reference || selectedExpense.card_number || selectedExpense.account_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method Specific Details */}
                {selectedExpense.mode_of_payment === 'cash' && selectedExpense.cash_breakdown && Object.keys(selectedExpense.cash_breakdown).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Cash Breakdown</label>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => {
                          const count = selectedExpense.cash_breakdown[`${denomination}_notes`] || 0;
                          if (count > 0) {
                            return (
                              <div key={denomination} className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="text-center">
                                  <span className="text-sm font-medium text-gray-700 block mb-1">{currencySymbol} {denomination}</span>
                                  <span className="text-lg font-bold text-green-600">{count} note{count !== 1 ? 's' : ''}</span>
                                  <span className="text-xs font-medium text-gray-900 block mt-1">
                                    {formatCurrency(count * denomination)}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <div className="text-center">
                          <span className="text-lg font-bold text-green-600">
                            Total: {formatCurrency(Object.keys(selectedExpense.cash_breakdown).reduce((total, key) => {
                              const denomination = parseInt(key.split('_')[0]);
                              const count = selectedExpense.cash_breakdown[key] || 0;
                              return total + (denomination * count);
                            }, 0))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedExpense.mode_of_payment === 'bank_transfer' && (selectedExpense.bank_name || selectedExpense.account_name || selectedExpense.account_number) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Bank Transfer Details</label>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedExpense.bank_name && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Bank Name</label>
                            <p className="text-gray-900 font-medium">{selectedExpense.bank_name}</p>
                          </div>
                        )}
                        {selectedExpense.account_name && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Account Name</label>
                            <p className="text-gray-900 font-medium">{selectedExpense.account_name}</p>
                          </div>
                        )}
                        {selectedExpense.account_number && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Account Number</label>
                            <p className="text-gray-900 font-mono">{selectedExpense.account_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedExpense.mode_of_payment === 'mobile_money' && (selectedExpense.mobile_network || selectedExpense.sender_mobile_number || selectedExpense.receiver_mobile_number || selectedExpense.transaction_reference) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Mobile Money Details</label>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedExpense.mobile_network && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Network</label>
                            <p className="text-gray-900 font-medium">{selectedExpense.mobile_network}</p>
                          </div>
                        )}
                        {selectedExpense.sender_mobile_number && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Sender Number</label>
                            <p className="text-gray-900 font-mono">{selectedExpense.sender_mobile_number}</p>
                          </div>
                        )}
                        {selectedExpense.receiver_mobile_number && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Receiver Number</label>
                            <p className="text-gray-900 font-mono">{selectedExpense.receiver_mobile_number}</p>
                          </div>
                        )}
                        {selectedExpense.transaction_reference && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Transaction Reference</label>
                            <p className="text-gray-900 font-mono">{selectedExpense.transaction_reference}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedExpense.mode_of_payment === 'card' && selectedExpense.card_number && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Card Details</label>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedExpense.card_number && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Card Number</label>
                            <p className="text-gray-900 font-mono">{selectedExpense.card_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Description */}
                {selectedExpense.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <div className="p-3 bg-gray-50 rounded-lg border min-h-[100px]">
                      <p className="text-gray-900">{selectedExpense.description}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setShowPrintModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 shadow-md"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Receipt
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditExpense(selectedExpense);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 shadow-md"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-t-xl flex items-center justify-between">
                <h3 className="text-lg font-semibold">Edit Expense</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Paid To / Received By *</label>
                    <input
                      type="text"
                      value={expenseForm.description}
                      onChange={(e) => handleExpenseFormChange('description', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter person or entity name"
                    />
                    {formErrors.description && <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => handleExpenseFormChange('amount', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.amount ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                    {formErrors.amount && <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={expenseForm.category}
                      onChange={(e) => handleExpenseFormChange('category', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.category ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select category</option>
                      {expenseCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    {formErrors.category && <p className="mt-1 text-sm text-red-600">{formErrors.category}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <select
                      value={expenseForm.payment_method}
                      onChange={(e) => handleExpenseFormChange('payment_method', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="card">Card</option>
                      <option value="cheque">Cheque</option>
                    </select>
                    {formErrors.payment_method && <p className="mt-1 text-sm text-red-600">{formErrors.payment_method}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expense Date *</label>
                    <input
                      type="date"
                      value={expenseForm.expense_date}
                      onChange={(e) => handleExpenseFormChange('expense_date', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                        formErrors.expense_date ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.expense_date && <p className="mt-1 text-sm text-red-600">{formErrors.expense_date}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={expenseForm.notes}
                      onChange={(e) => handleExpenseFormChange('notes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                      placeholder="Notes (optional)"
                    />
                  </div>
                </div>

                {/* Dynamic Payment Method Fields */}
                <div className="mt-6">
                  {/* Cash Breakdown */}
                  {expenseForm.payment_method === 'cash' && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{currencySymbol}</span>
                        </span>
                        Cash Breakdown
                      </h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => (
                          <div key={denomination} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="text-center">
                              <span className="text-sm font-medium text-gray-700 block mb-2">{currencySymbol} {denomination}</span>
                              <input
                                type="number"
                                min="0"
                                value={expenseForm.cash_breakdown[`${denomination}_notes`] || 0}
                                onChange={(e) => updateCashBreakdown(denomination, e.target.value)}
                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                              />
                              <span className="text-xs font-medium text-gray-900 block mt-1">
                                {formatCurrency((expenseForm.cash_breakdown[`${denomination}_notes`] || 0) * denomination)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <div className="text-center">
                          <span className="text-lg font-bold text-green-600">
                            Total: {formatCurrency(Object.keys(expenseForm.cash_breakdown).reduce((total, key) => {
                              const denomination = parseInt(key.split('_')[0]);
                              const count = expenseForm.cash_breakdown[key] || 0;
                              return total + (denomination * count);
                            }, 0))}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Fields */}
                  {expenseForm.payment_method === 'bank_transfer' && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">🏦</span>
                        </span>
                        Bank Transfer Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={expenseForm.bank_name}
                            onChange={(e) => handleExpenseFormChange('bank_name', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter bank name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Name
                          </label>
                          <input
                            type="text"
                            value={expenseForm.account_name}
                            onChange={(e) => handleExpenseFormChange('account_name', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter account name"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.account_number}
                            onChange={(e) => handleExpenseFormChange('account_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter account number"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mobile Money Fields */}
                  {expenseForm.payment_method === 'mobile_money' && (
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">📱</span>
                        </span>
                        Mobile Money Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mobile Network
                          </label>
                          <select
                            value={expenseForm.mobile_network}
                            onChange={(e) => handleExpenseFormChange('mobile_network', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          >
                            <option value="">Select Network</option>
                            <option value="mtn">MTN</option>
                            <option value="vodafone">Vodafone</option>
                            <option value="airteltigo">AirtelTigo</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sender Mobile Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.sender_mobile_number}
                            onChange={(e) => handleExpenseFormChange('sender_mobile_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter sender number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Receiver Mobile Number
                          </label>
                          <input
                            type="text"
                            value={expenseForm.receiver_mobile_number}
                            onChange={(e) => handleExpenseFormChange('receiver_mobile_number', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter receiver number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Transaction Reference
                          </label>
                          <input
                            type="text"
                            value={expenseForm.transaction_reference}
                            onChange={(e) => handleExpenseFormChange('transaction_reference', e.target.value)}
                            className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            placeholder="Enter transaction reference"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card Payment Fields */}
                  {expenseForm.payment_method === 'card' && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">💳</span>
                        </span>
                        Card Payment Details
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Number (Last 4 digits)
                        </label>
                        <input
                          type="text"
                          value={expenseForm.card_number}
                          onChange={(e) => handleExpenseFormChange('card_number', e.target.value)}
                          className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          placeholder="Enter last 4 digits"
                          maxLength="4"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setFormErrors({});
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExpenseSubmit}
                  disabled={formLoading}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md"
                >
                  {formLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4" />
                      <span>Update Expense</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Modal */}
        {showPrintModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-t-xl flex items-center justify-between">
                <h3 className="text-lg font-semibold">Print Expense Receipt</h3>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">EXPENSE RECEIPT</h2>
                    <div className="text-sm text-gray-600 mt-2">
                      Date: {new Date(selectedExpense.expense_date).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="font-medium">Paid To:</span>
                      <span>{selectedExpense.paid_to}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Amount:</span>
                      <span className="font-bold">{formatCurrency(selectedExpense.amount || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Category:</span>
                      <span>{selectedExpense.expense_account}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Payment Method:</span>
                      <span>{paymentMethods.find(m => m.value === selectedExpense.mode_of_payment)?.label || selectedExpense.mode_of_payment}</span>
                    </div>

                    {/* Cash Breakdown */}
                    {selectedExpense.mode_of_payment === 'cash' && selectedExpense.cash_breakdown && Object.keys(selectedExpense.cash_breakdown).length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="font-medium mb-2 text-center">Cash Breakdown</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => {
                            const count = selectedExpense.cash_breakdown[`${denomination}_notes`] || 0;
                            if (count > 0) {
                              return (
                                <div key={denomination} className="flex justify-between bg-white p-2 rounded border">
                                  <span>{currencySymbol} {denomination}:</span>
                                  <span className="font-medium">{count} note{count !== 1 ? 's' : ''} = {formatCurrency(count * denomination)}</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <div className="mt-3 pt-2 border-t border-gray-300 text-center">
                          <span className="font-bold text-green-600">
                            Total: {formatCurrency(Object.keys(selectedExpense.cash_breakdown).reduce((total, key) => {
                              const denomination = parseInt(key.split('_')[0]);
                              const count = selectedExpense.cash_breakdown[key] || 0;
                              return total + (denomination * count);
                            }, 0))}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Bank Transfer Details */}
                    {selectedExpense.mode_of_payment === 'bank_transfer' && (selectedExpense.bank_name || selectedExpense.account_name || selectedExpense.account_number) && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="font-medium mb-2 text-center">Bank Transfer Details</div>
                        {selectedExpense.bank_name && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Bank Name:</span>
                            <span>{selectedExpense.bank_name}</span>
                          </div>
                        )}
                        {selectedExpense.account_name && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Account Name:</span>
                            <span>{selectedExpense.account_name}</span>
                          </div>
                        )}
                        {selectedExpense.account_number && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Account Number:</span>
                            <span className="font-mono">{selectedExpense.account_number}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mobile Money Details */}
                    {selectedExpense.mode_of_payment === 'mobile_money' && (selectedExpense.mobile_network || selectedExpense.sender_mobile_number || selectedExpense.receiver_mobile_number || selectedExpense.transaction_reference) && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="font-medium mb-2 text-center">Mobile Money Details</div>
                        {selectedExpense.mobile_network && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Network:</span>
                            <span>{selectedExpense.mobile_network}</span>
                          </div>
                        )}
                        {selectedExpense.sender_mobile_number && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Sender Number:</span>
                            <span className="font-mono">{selectedExpense.sender_mobile_number}</span>
                          </div>
                        )}
                        {selectedExpense.receiver_mobile_number && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Receiver Number:</span>
                            <span className="font-mono">{selectedExpense.receiver_mobile_number}</span>
                          </div>
                        )}
                        {selectedExpense.transaction_reference && (
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Transaction Reference:</span>
                            <span className="font-mono">{selectedExpense.transaction_reference}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Card Details */}
                    {selectedExpense.mode_of_payment === 'card' && selectedExpense.card_number && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="font-medium mb-2 text-center">Card Details</div>
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Card Number:</span>
                          <span className="font-mono">{selectedExpense.card_number}</span>
                        </div>
                      </div>
                    )}

                    {selectedExpense.description && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="font-medium mb-2">Description:</div>
                        <div className="text-sm text-gray-600">{selectedExpense.description}</div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => {
                      window.print();
                    }}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 shadow-md"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => setShowPrintModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-t-xl">
                <h3 className="text-lg font-semibold">Delete Expense</h3>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete this expense? This action cannot be undone.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{selectedExpense.paid_to}</span>
                    <span className="font-bold">{formatCurrency(selectedExpense.amount || 0)}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {selectedExpense.expense_account} • {new Date(selectedExpense.expense_date).toLocaleDateString()}
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteExpense}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-md"
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
    </Layout>
  );
};

export default Expenses;
